import React, { useState, useEffect } from "react";
import { Text } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";

const manualTagOrder = [
  "like",
  "veryGood",
  "good",
  "finished",
  "bad",
  "inProgress",
  "todo",
];
const manualTagOrderIndex = manualTagOrder.reduce((acc, name, index) => {
  acc[name] = index;
  return acc;
}, {});

// Families that render collapsed (first N + selected) with a "+ show more" chip.
const COLLAPSED_FIRST_N = 2;

const SelectableTags = ({ relevantTags, alreadySelectedTags = [], onSelectionChange, language }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const [mobileExpanded, setMobileExpanded] = useState(false);

  useEffect(() => {
    setSelectedTags(alreadySelectedTags);
  }, [alreadySelectedTags]);

  const toggleTagSelection = (selectedTag) => {
    let updatedSelection;
    if (selectedTags.find((t) => t.tag.id === selectedTag.tag.id)) {
      updatedSelection = selectedTags.filter((t) => t.tag.id !== selectedTag.tag.id);
    } else {
      updatedSelection = [...selectedTags, selectedTag];
    }
    setSelectedTags(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedTagIds = new Set(selectedTags.map((tagObject) => tagObject.tag.id));

  // Family buckets — backend order preserved.
  const familyByType = (type) => relevantTags.filter((t) => t.tag.type === type);

  // Rating (type=3) sorted by manualTagOrder so like/veryGood/good/... stays consistent.
  const ratingChips = familyByType(3).sort((a, b) => {
    const aIndex = manualTagOrderIndex[a.tag.name] ?? 999;
    const bIndex = manualTagOrderIndex[b.tag.name] ?? 999;
    return aIndex - bIndex;
  });
  const personalRecomChips = familyByType(2);
  const kindOrder = ["movie", "book", "game", "youtubechannel"];
  const kindOrderIndex = kindOrder.reduce((acc, n, i) => { acc[n] = i; return acc; }, {});
  const kindChips = familyByType(4).sort((a, b) => {
    const aI = kindOrderIndex[a.tag.name] ?? 999;
    const bI = kindOrderIndex[b.tag.name] ?? 999;
    return aI - bI;
  });
  const subCategoryChips = familyByType(6);
  const genreAll = familyByType(5);
  const yearAll = familyByType(7);
  const customAll = familyByType(1);

  // Collapsed families: show all selected + top-N unselected. Everything
  // else moves to extras behind "+ Показать больше".
  const splitCollapsed = (all) => {
    const selected = all.filter((t) => selectedTagIds.has(t.tag.id));
    const unselected = all.filter((t) => !selectedTagIds.has(t.tag.id));
    const room = Math.max(COLLAPSED_FIRST_N - selected.length, 0);
    const visible = [...selected, ...unselected.slice(0, room)];
    const visibleIds = new Set(visible.map((t) => t.tag.id));
    const extras = all.filter((t) => !visibleIds.has(t.tag.id));
    return { visible, extras };
  };

  const genre = splitCollapsed(genreAll);
  const year = splitCollapsed(yearAll);
  const custom = splitCollapsed(customAll);

  const renderTagChip = (tagObject) => {
    const { name } = tagObject.tag;
    const { count } = tagObject;
    const isSelected = selectedTags.find((t) => t.tag.id === tagObject.tag.id);
    const selectedStyles = isSelected ? {
      root: { background: '#0A84FF', borderColor: 'transparent' },
      label: { color: '#ffffff' },
    } : undefined;
    return (
      <TagBadge
        key={tagObject.tag.id}
        component="button"
        type="button"
        className="cursor-pointer flex-shrink-0"
        size="md"
        styles={selectedStyles}
        onClick={() => toggleTagSelection(tagObject)}
      >
        {tagObject.tag.imgUrl !== "" ? (
          <div className="flex items-center text-center max-h-6 gap-1">
            <span>#</span>
            <img className="w-5 max-w-none h-5 rounded-full" src={tagObject.tag.imgUrl} />
            <span>{tagName(name, language)}</span>
            <Text span size="xs" c="inherit" style={{ opacity: 0.55, marginLeft: 4 }}>({count})</Text>
          </div>
        ) : (
          <>
            {tagName(name, language)}
            <Text span size="xs" c="inherit" style={{ opacity: 0.55, marginLeft: 4 }}>({count})</Text>
          </>
        )}
      </TagBadge>
    );
  };

  const renderExpandChip = (key, extrasCount, expanded) => {
    const wordShowMore = language === "ru" ? "Показать больше" : "Show more";
    const wordHide = language === "ru" ? "Скрыть" : "Hide";
    return (
      <TagBadge
        component="button"
        type="button"
        size="md"
        className="cursor-pointer flex-shrink-0"
        styles={{ label: { fontStyle: 'italic', opacity: 0.75 } }}
        onClick={() => toggleGroup(key)}
      >
        {expanded ? (
          <>
            <span className="sm:hidden">−</span>
            <span className="hidden sm:inline">− {wordHide}</span>
          </>
        ) : (
          <>
            <span className="sm:hidden">+ ({extrasCount})</span>
            <span className="hidden sm:inline">+ {wordShowMore} ({extrasCount})</span>
          </>
        )}
      </TagBadge>
    );
  };

  const renderRow = (chips, marginClass = "mb-2") => (chips.length === 0 ? null : (
    <div className={`flex flex-wrap gap-2 ${marginClass}`}>
      {chips.map(renderTagChip)}
    </div>
  ));

  const renderCollapsibleFamily = (key, split, marginClass = "mb-2") => {
    if (split.visible.length === 0 && split.extras.length === 0) return null;
    const expanded = openGroups.has(key);
    return (
      <div className={`flex flex-wrap gap-2 ${marginClass}`}>
        {split.visible.map(renderTagChip)}
        {expanded && split.extras.map(renderTagChip)}
        {split.extras.length > 0 && renderExpandChip(key, split.extras.length, expanded)}
      </div>
    );
  };

  return (
    <div className="py-2">
      {renderRow([...ratingChips, ...personalRecomChips], "mb-5")}
      {/* Mobile-only toggle: hides the non-rating families until asked for. */}
      <div className="sm:hidden mb-4 flex flex-wrap">
        <TagBadge
          component="button"
          type="button"
          size="md"
          className="cursor-pointer flex-shrink-0"
          styles={{ label: { fontStyle: 'italic', opacity: 0.75 } }}
          onClick={() => setMobileExpanded((v) => !v)}
        >
          {mobileExpanded
            ? `− ${language === "ru" ? "Скрыть дополнительные теги" : "Hide extra tags"}`
            : `+ ${language === "ru" ? "Показать дополнительные теги" : "Show extra tags"}`}
        </TagBadge>
      </div>
      <div className={mobileExpanded ? "" : "hidden sm:block"}>
        {renderRow(kindChips)}
        {renderRow(subCategoryChips)}
        {renderCollapsibleFamily("genre", genre)}
        {renderCollapsibleFamily("year", year)}
        {renderCollapsibleFamily("custom", custom)}
      </div>
    </div>
  );
};

export default SelectableTags;
